import { updateValuesWrapper } from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { log, PushProcessingNotification } from "@repo/helpers";
import { Col } from "@repo/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";

import { useDocumentationApi } from "../../api/documentationApi";
import * as Queries from "../../api/queries";
import { useRepOnboardingQuery } from "../../api/repApi";
import { EditableItemDynamicTab } from "../../components/dynamicEditor/dynamicTab/editableItem";
import { ITemplateData } from "../../components/dynamicEditor/templateDataHook";
import { SingleOnboardingCompletion } from "../../components/onboardingCompletion";
import { DocumentationProvider, useDocumentationContext, useRepContext } from "../../contexts/editableItemContext";
import { Documentation } from "../../models";
import { tabs } from "../../string";

function DocumentationPage(props: Props) {
	return (
		<DocumentationProvider>
			<DocumentationInternal {...props} />
		</DocumentationProvider>
	);
}

function DocumentationInternal(props: Props) {
	const repContext = useRepContext();
	const { repId, repLoaded } = repContext;
	const queryClient = useQueryClient();

	const { checkOnboarding } = useRepOnboardingQuery();
	const documentationContext = useDocumentationContext();
	const { item: repDocumentation, overwriteItem: setRepDocumentation } = documentationContext;
	const { loadRepDocumentation, useDocumentationQuery, saveRepDocumentation } = useDocumentationApi();
	const { data: documentation, clearQuery: clearDocumentationQuery } = useDocumentationQuery(repId);
	const { mutateAsync: saveMutation } = useMutation({
		mutationFn: (variables: { documentation: Documentation; repId: number; signal?: AbortSignal }) =>
			saveRepDocumentation(variables.documentation, variables.repId, { signal: variables.signal }),
	});

	const templateData = useMemo(() => ({ documentation: repDocumentation }) as IDocumentationData, [repDocumentation]);

	const allLoaded = useMemo(() => repDocumentation && repLoaded, [repDocumentation, repLoaded]);

	useEffect(() => {
		async function loadDocumentation() {
			setRepDocumentation(documentation);
		}
		if (!repDocumentation) loadDocumentation();
	}, [repId, documentation]);

	const save = useCallback(async () => {
		let notification = PushProcessingNotification("Saving Documentation");
		try {
			const controller = new AbortController();
			await saveMutation({ documentation: repDocumentation, repId, signal: controller.signal });
			notification.processing("Checking Status...");
			await checkOnboarding();
			notification.processing("Loading...");
			const saved = await loadRepDocumentation(repId)();
			clearDocumentationQuery();
			await queryClient.invalidateQueries({ queryKey: [Queries.SETTINGS_REP] });
			await queryClient.invalidateQueries({
				queryKey: [Queries.BACKGROUND_CHECKS],
			});
			await queryClient.invalidateQueries({
				queryKey: [Queries.IDENTIFICATION_REQUESTS],
			});
			notification.success("Saved!");
			return saved;
		} catch (ex) {
			notification.error("Error saving");
			log(ex, "error");
		}
	}, [repDocumentation, repId, checkOnboarding, queryClient, loadRepDocumentation, saveMutation]);

	const updateValues = useMemo(() => updateValuesWrapper(applyBusinessLogic), [applyBusinessLogic]);
	async function applyBusinessLogic(id: string, value: any, newState: IDocumentationData) {
		return newState;
	}

	return (
		<>
			<Col md={12} tag="section" id="onboarding">
				<SingleOnboardingCompletion req={"documentation"} depthToDisplay={0} />
			</Col>
			<EditableItemDynamicTab
				itemContext={documentationContext}
				pageName={tabs.documentation}
				otherTemplateData={templateData}
				readonly={props.readOnly}
				itemProp="documentation"
				updateValues={updateValues}
				save={save}
				loaded={allLoaded}
			/>
		</>
	);
}

export interface IDocumentationData extends ITemplateData {
	documentation: Documentation;
}

interface Props {
	readOnly?: boolean;
}

export default DocumentationPage;
